import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Stack, Button, Typography, Paper, CircularProgress, Box, Alert, Chip, TextField,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import LoadingComponent from "../../components/LoadingComponent";
import ModeContrastPairPlot from "../../components/ModeContrastPairPlot";
import ModeContrastResultTable from "../../components/ModeContrastResultTable";
import ModeContrastGroups, {
    DIRECTION_COLORS, directionKey, directionKind, directionLabel, selectedText,
} from "../../components/ModeContrastGroups";
import { modeLabel } from "../../helper/repeatabilityText";
import {
    MODE_CONTRAST_SELECTION, SELECTION_KINDS, isSelectionFinalized, selectionResumeRoute, selectionSidebar,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";

const cardSx = {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    m: 0,
    boxSizing: "border-box",
    p: { xs: 2, sm: 2.5 },
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
};

const filled = (value) => String(value ?? "").trim() !== "";
const validCount = (value) => filled(value) && Number.isInteger(Number(value)) && Number(value) >= 0;
const validLimit = (value) => filled(value) && Number.isFinite(Number(value)) && Number(value) > 0;

/** The ΔE00 limit fields of a category (every other field is its count). */
export function limitFields(entry) {
    const kind = directionKind(entry);
    if (kind === "both") return ["max_mode_1", "max_mode_2"];
    if (kind === "stable") return ["max_between"];
    return ["max_close", "min_far"];
}

/** The typed values as the API's directions, or null while one is invalid. */
export function parseDirections(inputs) {
    if (!inputs.length) return null;
    const valid = inputs.every((entry) => validCount(entry.count) && limitFields(entry).every((f) => validLimit(entry[f])));
    if (!valid) return null;
    return inputs.map((entry) => ({
        kind: directionKind(entry),
        ...(directionKind(entry) === "contrast" ? { close_mode: entry.close_mode } : {}),
        count: Number(entry.count),
        ...Object.fromEntries(limitFields(entry).map((f) => [f, Number(entry[f])])),
    }));
}

// Step 7 of the mode contrast selection: groups of palette samples that are
// close in one capture mode and far apart in the other (both directions),
// groups close in both modes, and single samples whose colour is stable
// across the modes. Changing a limit re-evaluates the groups (nothing is
// re-measured); Confirm reduces the palette to the selected samples and
// finishes the selection.
export default function ColorSelectionModeContrast() {
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [status, setStatus] = useState(null);
    /** api/selection/modecontrast payload. */
    const [result, setResult] = useState(null);
    /** Per category the values as typed (strings). */
    const [inputs, setInputs] = useState([]);
    const [applying, setApplying] = useState(false);
    const timer = useRef(null);
    const requestId = useRef(0);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const navigate = useNavigate();

    const { selectionId, stepRoute } = useSelectionParams();
    const listRoute = SELECTION_KINDS.modeContrast.listRoute;
    const overviewRoute = stepRoute("overview", MODE_CONTRAST_SELECTION);

    const adoptSettings = (payload) => {
        setInputs(payload.settings.directions.map((d) => ({
            kind: directionKind(d),
            close_mode: d.close_mode,
            count: String(d.count),
            ...Object.fromEntries(limitFields(d).map((f) => [f, String(d[f])])),
        })));
    };

    useEffect(() => {
        const load = async () => {
            try {
                const selectionResponse = await axios.get(`api/color/selection/${selectionId}/`);
                const current = selectionResponse.data.status;
                if (isSelectionFinalized(current)) {
                    navigate(overviewRoute, { replace: true });
                    return;
                }
                setStatus(current);
                if ((current || "").toUpperCase() !== "MODE_CONTRAST") return;
                const response = await axios.post("api/selection/modecontrast/results", { selectionId });
                const payload = response.data.modeContrast || null;
                setResult(payload);
                if (payload) adoptSettings(payload);
            } catch (error) {
                console.error("Error loading the Mode Contrast Filter:", error);
                setErrorMessage(error?.response?.data?.error || "Loading the Mode Contrast Filter failed.");
            } finally {
                setLoading(false);
            }
        };
        load();
        return () => clearTimeout(timer.current);
    }, []);

    const applySettings = async (directions) => {
        const id = requestId.current + 1;
        requestId.current = id;
        setApplying(true);
        setErrorMessage(null);
        try {
            const response = await axios.post("api/selection/modecontrast/settings", { selectionId, directions });
            // a later change supersedes this answer
            if (id === requestId.current) setResult(response.data.modeContrast);
        } catch (error) {
            console.error("Error applying the limits:", error);
            if (id === requestId.current) {
                setErrorMessage(error?.response?.data?.error || "Applying the limits failed.");
            }
        } finally {
            if (id === requestId.current) setApplying(false);
        }
    };

    // Typing re-evaluates the groups after a short pause; invalid values are not sent.
    const changeInput = (index, field, value) => {
        const next = inputs.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry));
        setInputs(next);
        clearTimeout(timer.current);
        const directions = parseDirections(next);
        if (!directions) return;
        timer.current = setTimeout(() => applySettings(directions), 400);
    };

    const confirmFilter = async () => {
        clearTimeout(timer.current);
        setConfirming(true);
        setErrorMessage(null);
        try {
            await axios.post("api/selection/modecontrast/confirm", {
                selectionId, directions: parseDirections(inputs),
            });
            setConfirmOpen(false);
            navigate(overviewRoute);
        } catch (error) {
            console.error("Error applying the Mode Contrast Filter:", error);
            setConfirmOpen(false);
            setErrorMessage(error?.response?.data?.error || "Applying the Mode Contrast Filter failed.");
        } finally {
            setConfirming(false);
        }
    };

    const open = (status || "").toUpperCase() === "MODE_CONTRAST";
    const counts = result?.counts;
    const inputsValid = parseDirections(inputs) !== null;
    const directionOf = (entry) => result?.directions.find((d) => directionKey(d) === directionKey(entry));

    /** The limit inputs of a category: label, aria-label and field per input. */
    const limitInputs = (entry) => {
        const kind = directionKind(entry);
        if (kind === "both") {
            return [
                { field: "max_mode_1", label: `Max ΔE00 — ${modeLabel(1)}`, aria: "Max dE00 mode 1 (close in both modes)" },
                { field: "max_mode_2", label: `Max ΔE00 — ${modeLabel(2)}`, aria: "Max dE00 mode 2 (close in both modes)" },
            ];
        }
        if (kind === "stable") {
            return [{ field: "max_between", label: "Max ΔE00 between modes", aria: "Max dE00 between modes (stable)" }];
        }
        const farMode = 3 - entry.close_mode;
        return [
            { field: "max_close", label: `Max ΔE00 — ${modeLabel(entry.close_mode)}`, aria: `Max dE00 mode ${entry.close_mode} (close in mode ${entry.close_mode})` },
            { field: "min_far", label: `Min ΔE00 — ${modeLabel(farMode)}`, aria: `Min dE00 mode ${farMode} (close in mode ${entry.close_mode})` },
        ];
    };
    const countAria = (entry) => {
        const kind = directionKind(entry);
        if (kind === "both") return "Number of pairs close in both modes";
        if (kind === "stable") return "Number of stable samples";
        return `Number of pairs close in mode ${entry.close_mode}`;
    };

    /** The category's result chips: what it selected and how many qualify. */
    const resultChips = (direction) => {
        if (directionKind(direction) === "stable") {
            return [
                `${direction.selected} of ${direction.count} samples`,
                `${direction.candidate_samples} samples within the limit`,
            ];
        }
        return [
            `${direction.groups.length} of ${direction.count} pairs · ${direction.selected} samples`,
            `${direction.candidate_pairs} pairs within the limits`,
            `${direction.samples_with_partner} samples with a partner`,
        ];
    };

    const confirmText = () => {
        if (!counts) return "";
        const parts = (result?.directions || [])
            .filter((d) => d.selected > 0)
            .map((d) => (directionKind(d) === "stable"
                ? `${d.selected} sample${d.selected === 1 ? "" : "s"} ${directionLabel(d).toLowerCase()}`
                : `${d.groups.length} pair${d.groups.length === 1 ? "" : "s"} ${directionLabel(d).toLowerCase()}`));
        const detail = parts.length ? `: ${parts.join(" · ")}` : "";
        return `${counts.kept} of ${counts.input} samples remain${detail}. The palette is reduced to these samples and the selection is finalized.`;
    };

    return (
        <>
            <Box sx={{ width: "100%", maxWidth: "100%", mt: "2%", mb: 4, boxSizing: "border-box" }}>
                <ExplorationPageShell
                    title="Mode Contrast Filter"
                    subtitle="Pairs of palette samples that are close in one capture mode and far apart in the other, or close in both modes, and single samples that are stable across the modes. Confirm reduces the palette to the selected samples."
                    noPaper
                    stepSidebar={selectionSidebar("modecontrast", counts ? counts.kept : null, MODE_CONTRAST_SELECTION)}
                    contentBoxSx={{
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        px: 0,
                        py: { xs: 2, md: 3 },
                    }}
                >
                    <Stack spacing={3} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, mt: 1, alignItems: "stretch" }}>
                        {errorMessage && <Alert severity="error" sx={{ borderRadius: 2 }}>{errorMessage}</Alert>}

                        {loading ? (
                            <LoadingComponent />
                        ) : !open ? (
                            <Alert
                                severity="info" sx={{ borderRadius: 2 }}
                                action={
                                    <Button size="small" onClick={() => navigate(selectionResumeRoute(selectionId, status, MODE_CONTRAST_SELECTION))} sx={{ textTransform: "none" }}>
                                        Open the current step
                                    </Button>
                                }
                            >
                                The spatial repeatability is not confirmed yet.
                            </Alert>
                        ) : result && (
                            <>
                                <Paper elevation={0} sx={cardSx}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Typography variant="subtitle1" fontWeight={700}>Limits</Typography>
                                        {applying ? <CircularProgress size={18} thickness={5} aria-label="Applying limits" /> : null}
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        A pair is within the ΔE00 limits of its category; a sample is in at most one pair of a category. A stable sample's own mode 1 and mode 2 readings are within the limit. A count of 0 turns a category off.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "grid", gap: 2, mt: 2,
                                            gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
                                        }}
                                    >
                                        {inputs.map((entry, index) => {
                                            const direction = directionOf(entry);
                                            const key = directionKey(entry);
                                            return (
                                                <Box
                                                    key={key}
                                                    data-testid={`mode-contrast-limits-${key}`}
                                                    sx={{
                                                        border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2,
                                                        borderTop: `3px solid ${DIRECTION_COLORS[key]}`,
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" fontWeight={700}>
                                                        {directionLabel({ ...entry, far_mode: 3 - entry.close_mode })}
                                                    </Typography>
                                                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                                        <TextField
                                                            size="small" type="number" label={directionKind(entry) === "stable" ? "Number of samples" : "Number of pairs"}
                                                            value={entry.count}
                                                            onChange={(e) => changeInput(index, "count", e.target.value)}
                                                            inputProps={{ min: 0, step: 1, "aria-label": countAria(entry) }}
                                                            error={!validCount(entry.count)}
                                                            sx={{ width: 150 }}
                                                        />
                                                        {limitInputs(entry).map((input) => (
                                                            <TextField
                                                                key={input.field}
                                                                size="small" type="number" label={input.label}
                                                                value={entry[input.field]}
                                                                onChange={(e) => changeInput(index, input.field, e.target.value)}
                                                                inputProps={{ min: 0.1, step: 0.5, "aria-label": input.aria }}
                                                                error={!validLimit(entry[input.field])}
                                                                sx={{ width: 190 }}
                                                            />
                                                        ))}
                                                    </Stack>
                                                    {direction && (
                                                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                                            {resultChips(direction).map((label, k) => (
                                                                <Chip
                                                                    key={label} size="small" variant="outlined"
                                                                    color={k === 0 ? "primary" : "default"} label={label}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Paper>

                                <Paper elevation={0} sx={cardSx}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Result per category</Typography>
                                    <ModeContrastResultTable directions={result.directions} />
                                </Paper>

                                <Paper elevation={0} sx={cardSx}>
                                    <Typography variant="subtitle1" fontWeight={700}>Pair map</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                        Every pair of palette samples at its ΔE00 in mode 1 and in mode 2, one pair category at a time (pick it above the map): shaded, the category's limits; small points, pairs within them; large points, pairs inside a selected group. Stable samples are single samples, not pairs: see the table above and the groups below.
                                    </Typography>
                                    <ModeContrastPairPlot pairs={result.pairs} directions={result.directions} />
                                </Paper>

                                <Paper elevation={0} sx={cardSx}>
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                                        <Typography variant="subtitle1" fontWeight={700}>Pairs and stable samples</Typography>
                                        <Chip size="small" color="primary" label={`${counts.kept} of ${counts.input} samples retained`} />
                                    </Stack>
                                    <ModeContrastGroups directions={result.directions} />
                                </Paper>

                                <Typography variant="body2" color="text.secondary">
                                    {`Confirm reduces the palette to ${counts.kept} of ${counts.input} samples and finalizes the selection.`}
                                </Typography>
                            </>
                        )}

                        <NavBarBottom
                            variant="embedded"
                            // The repeatability is confirmed: the earlier steps are locked.
                            hideReturnButton
                            prevPage={listRoute}
                            nextPage={overviewRoute}
                            nextLabel="Confirm"
                            enableNextPage={!open || !result || !counts || counts.kept === 0 || !inputsValid || applying}
                            nextLoading={confirming}
                            nextPageClickFunc={() => setConfirmOpen(true)}
                        />
                    </Stack>
                </ExplorationPageShell>
            </Box>

            <Dialog open={confirmOpen} onClose={() => !confirming && setConfirmOpen(false)}>
                <DialogTitle>Apply the Mode Contrast Filter?</DialogTitle>
                <DialogContent>
                    <DialogContentText>{confirmText()}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        disabled={confirming}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={confirmFilter}
                        disabled={confirming}
                        startIcon={confirming ? <CircularProgress size={16} thickness={5} /> : <CheckCircleIcon />}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                    >
                        Confirm and finish
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
